// Mon Profil - Page pour les ACHETEURS
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User, Mail, Phone, MapPin, ArrowLeft, Edit, Save, Camera,
  Bell, Lock, CreditCard, Package, Heart, LogOut, ChevronRight,
  Shield, Settings, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Section avec titre
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// Lien de menu
function MenuLink({ icon: Icon, label, description, onClick, color = '#6B7280' }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors rounded-xl"
    >
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <div className="flex-1 text-left">
        <p className="font-medium text-gray-900">{label}</p>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
      <ChevronRight size={18} className="text-gray-400" />
    </button>
  );
}

export default function MonProfilPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    firstName: 'Jean',
    lastName: 'Dupont',
    email: 'jean.dupont@email.com',
    phone: '06 12 34 56 78',
    address: '12 rue de la Paix',
    city: 'Paris',
    postalCode: '75002',
    avatar: null
  });

  const handleSave = () => {
    setEditing(false);
    toast.success('Profil mis à jour');
  };

  const stats = {
    orders: 12,
    favorites: 8,
    reviews: 5
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mon-profil-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Mon profil</h1>
            </div>
            {!editing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                className="rounded-lg"
              >
                <Edit size={14} className="mr-1" /> Modifier
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleSave}
                className="bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                <Save size={14} className="mr-1" /> Enregistrer
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Avatar et nom */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4 text-center">
          <div className="relative inline-block mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-3xl font-bold">
              {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
            </div>
            {editing && (
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                <Camera size={14} className="text-gray-600" />
              </button>
            )}
          </div>
          <h2 className="text-xl font-bold text-gray-900">{profile.firstName} {profile.lastName}</h2>
          <p className="text-gray-500">{profile.email}</p>

          {/* Stats */}
          <div className="flex justify-center gap-8 mt-6 pt-6 border-t border-gray-100">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.orders}</p>
              <p className="text-sm text-gray-500">Commandes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.favorites}</p>
              <p className="text-sm text-gray-500">Favoris</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.reviews}</p>
              <p className="text-sm text-gray-500">Avis</p>
            </div>
          </div>
        </div>

        {/* Informations personnelles */}
        <Section title="Informations personnelles">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <Input
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  disabled={!editing}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <Input
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  disabled={!editing}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                disabled={!editing}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <Input
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                disabled={!editing}
                className="rounded-xl"
              />
            </div>
          </div>
        </Section>

        {/* Adresse de livraison */}
        <Section title="Adresse de livraison">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <Input
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                disabled={!editing}
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                <Input
                  value={profile.postalCode}
                  onChange={(e) => setProfile({ ...profile, postalCode: e.target.value })}
                  disabled={!editing}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <Input
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  disabled={!editing}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>
        </Section>

        {/* Raccourcis */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <MenuLink
            icon={Package}
            label="Mes commandes"
            description="Voir l'historique de tes commandes"
            onClick={() => navigate('/mes-commandes')}
            color="#3B82F6"
          />
          <MenuLink
            icon={Heart}
            label="Mes favoris"
            description="Gérer tes produits favoris"
            onClick={() => navigate('/mes-favoris')}
            color="#EC4899"
          />
          <MenuLink
            icon={CreditCard}
            label="Moyens de paiement"
            description="Gérer tes cartes bancaires"
            onClick={() => toast.info('Fonctionnalité à venir')}
            color="#10B981"
          />
        </div>

        {/* Paramètres */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <MenuLink
            icon={Bell}
            label="Notifications"
            description="Gérer tes préférences"
            onClick={() => navigate('/parametres')}
            color="#F59E0B"
          />
          <MenuLink
            icon={Lock}
            label="Sécurité"
            description="Mot de passe et connexion"
            onClick={() => toast.info('Fonctionnalité à venir')}
            color="#6366F1"
          />
          <MenuLink
            icon={HelpCircle}
            label="Aide"
            description="Centre d'aide et FAQ"
            onClick={() => navigate('/service-client')}
            color="#0EA5E9"
          />
        </div>

        {/* Déconnexion */}
        <Button
          variant="outline"
          className="w-full rounded-xl text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => toast.info('Déconnexion...')}
        >
          <LogOut size={16} className="mr-2" />
          Se déconnecter
        </Button>
      </main>
    </div>
  );
}
